import { useCallback, useMemo, useRef, useState } from "react";
import { getModuleTagPillars } from "../kpiGridApi";
import { normalizeTagText } from "../../../../_constants/esgPillars";

const LOAD_FAILED = "Could not load the module tags";

/**
 * The module tag listing behind the ESG pillars panel. Loads on demand; a stale response is dropped, and the rows
 * already shown stay while a reload runs. Once loaded, it also counts the tags with no pillar, KPI tags with no keyword
 * row among them.
 * @returns {{ rows: object[], loaded: boolean, unsetCount: number, loading: boolean, error: string, load: () => Promise<void>, applyPillar: (moduleTag: string, esgPillar: string) => void }}
 */
export function useModuleTagPillars() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fetchIdRef = useRef(0);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError("");
    let res;
    try {
      res = await getModuleTagPillars();
    } catch {
      res = { isSuccess: false, data: { error: LOAD_FAILED } };
    }
    if (fetchId !== fetchIdRef.current) return;
    setLoading(false);
    if (!res?.isSuccess) {
      setError(res?.data?.error || LOAD_FAILED);
      return;
    }
    setRows(res.data.data);
    setLoaded(true);
  }, []);

  const applyPillar = useCallback((moduleTag, esgPillar) => {
    const target = normalizeTagText(moduleTag);
    setRows((current) =>
      current.map((row) => (normalizeTagText(row.keyword_value) === target ? { ...row, esg_pillar: esgPillar } : row)),
    );
  }, []);

  const unsetCount = useMemo(
    () => new Set(rows.filter((row) => !row.esg_pillar).map((row) => normalizeTagText(row.keyword_value))).size,
    [rows],
  );

  return { rows, loaded, unsetCount, loading, error, load, applyPillar };
}
